import * as React from "react";
import Slider, { Settings } from "react-slick";
import {
  ConstBreakPoint,
  ConstCalc,
} from "src/config/styles_const/styles_const";
import { ClientBookmark } from "src/domain/model/client_bookmark";
import { Customer } from "src/domain/model/customer";
import { TJobCareerWithCustomerID } from "src/domain/repository/job_career_repository";
import BtnLinkBorderSmall from "src/presentation/common/atom/btn/btn_link_border_small";
import SlideNextArrow from "src/presentation/common/atom/slider_arrow/slide_next_arrow";
import SlidePrevArrow from "src/presentation/common/atom/slider_arrow/slide_prev_arrow";
import { CampaignSQa } from "src/presentation/common/molecular/campaign/campaign-qa";
import { CampaignSSection } from "src/presentation/common/molecular/campaign/campaign-section-title";
import styled from "styled-components";
import { routeConfig } from "../../../../config/language/ja-JP/presentation/route_text";
import HeadlineSmall from "../../../common/atom/text/headline_small";
import PageWrapper from "../../../common/atom/wrapper/page_wrapper";
import Breadcrumbs from "../../../common/molecular/breadcrumbs/breadcrumbs";
import Progress from "../../../common/organism/progress";
import { TBreadcrumbItem } from "../../../type/common";
import SlideCustomerCard from "../top_page/slide_customer_card";

interface IProps {
  salesStrategyCustomers: Customer[];
  cooCustomers: Customer[];
  kpiCustomers: Customer[];
  partnerSalesCustomers: Customer[];
  allianceCustomers: Customer[];
  salesOrgDevCustomers: Customer[];
  sfaCrmCustomers: Customer[];
  salesEnablementCustomers: Customer[];
  salesDxCustomers: Customer[];
  insideSalesCustomers: Customer[];
  customerSuccessCustomers: Customer[];
  ltvCustomers: Customer[];
  groupSalesCustomers: Customer[];
  interDeptCustomers: Customer[];
  crossSellCustomers: Customer[];
  jobCareersWithCustomerID: TJobCareerWithCustomerID[];
  clientBookmarks: ClientBookmark[];
  isLoading: boolean;
}

// breackpoint は調整必要
const settings: Settings = {
  className: "slider",
  dots: false,
  infinite: false,
  centerMode: false,
  swipeToSlide: true,
  variableWidth: true,
  arrows: true,
  prevArrow: <SlidePrevArrow />,
  nextArrow: <SlideNextArrow />,
  slidesToShow: 4,
  slidesToScroll: 4,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 3,
      },
    },
    {
      breakpoint: 650,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
      },
    },

    {
      breakpoint: 450,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

interface ISubCategoryProps {
  title: string;
  moreLink: string;
  customers: Customer[];
  jobCareersWithCustomerID: TJobCareerWithCustomerID[];
  clientBookmarks: ClientBookmark[];
}

// 各サブカテゴリ（営業戦略・COO など）を全幅4枚スライダーで表示する
const SubCategorySlider: React.FC<ISubCategoryProps> = (props) => {
  const {
    title,
    moreLink,
    customers,
    jobCareersWithCustomerID,
    clientBookmarks,
  } = props;

  return (
    <SubCategoryWrapper>
      <SubTitleWrapper>
        <HeadlineSmall>{title}</HeadlineSmall>
        <BtnLinkBorderSmall to={moreLink} target="_blank">
          もっと見る
        </BtnLinkBorderSmall>
      </SubTitleWrapper>

      <SliderWrapper>
        <Slider {...settings}>
          {customers.map((customer, index) => {
            const jobCareerWithCustomerID = jobCareersWithCustomerID.find(
              (_jobCareerWithCustomerID) => {
                return _jobCareerWithCustomerID.customerID === customer.id;
              }
            );
            const jobCareers = jobCareerWithCustomerID
              ? jobCareerWithCustomerID.jobCareers
              : [];
            const isBookmarked: boolean = clientBookmarks.some(
              (clientBookmark: ClientBookmark) => {
                return clientBookmark.customerID === customer.id;
              }
            );

            return (
              <SlideCustomerWrapper
                key={`${customer.id}-${index}`}
                style={{ width: 200 }}
              >
                <SlideCustomerCard
                  customer={customer}
                  jobCareers={jobCareers}
                  isBookmarked={isBookmarked}
                />
              </SlideCustomerWrapper>
            );
          })}
        </Slider>
      </SliderWrapper>
    </SubCategoryWrapper>
  );
};

const ClientSalesCampaignComponent: React.FC<IProps> = (props) => {
  const {
    salesStrategyCustomers,
    cooCustomers,
    kpiCustomers,
    partnerSalesCustomers,
    allianceCustomers,
    salesOrgDevCustomers,
    sfaCrmCustomers,
    salesEnablementCustomers,
    salesDxCustomers,
    insideSalesCustomers,
    customerSuccessCustomers,
    ltvCustomers,
    groupSalesCustomers,
    interDeptCustomers,
    crossSellCustomers,
    jobCareersWithCustomerID,
    clientBookmarks,
    isLoading,
  } = props;

  const breadcrumbItems: TBreadcrumbItem[] = [
    routeConfig.clientRoot,
    routeConfig.clientSalesCampaign,
  ];

  // 各サブカテゴリ共通で渡す props をまとめる
  const common = { jobCareersWithCustomerID, clientBookmarks };

  const res: JSX.Element = (
    <React.Fragment>
      {isLoading && <Progress canAnimation={false} />}
      <PageWrapper>
        <Breadcrumbs breadcrumbItems={breadcrumbItems} />

        <CampaignSQa
          q="営業特集"
          a="営業といっても、新規開拓、既存顧客深耕、パートナーセールス、営業組織の効率化、事業部間連携など、企業のフェーズや課題によって求められる役割は異なります。そのため、経験してきた業界や商材、営業組織の規模、営業戦略の設計経験によって得意不得意があります。"
        />

        <div style={{ width: "100%", marginTop: 70 }} />

        <CampaignSSection
          index={"1"}
          title={"営業戦略を担う営業責任者・COO系"}
          text={
            "営業活動を属人的な個人プレーに依存させるのではなく、事業成長に直結する営業戦略を設計し、実行できる人材が求められます。市場選定、ターゲット設計、営業プロセス構築、KPI管理など、売上拡大のための全体設計を担うことが重要です。"
          }
        />

        <SubCategorySlider
          title="営業戦略"
          moreLink={`/client/scout/customers?q=営業戦略&p=1`}
          customers={salesStrategyCustomers}
          {...common}
        />
        <SubCategorySlider
          title="COO、事業責任者"
          moreLink={`/client/scout/customers?q=COO&p=1`}
          customers={cooCustomers}
          {...common}
        />
        <SubCategorySlider
          title="KPI設計"
          moreLink={`/client/scout/customers?q=KPI設計&p=1`}
          customers={kpiCustomers}
          {...common}
        />

        <NumberSectionWrapper />

        <CampaignSSection
          index={"2"}
          title={"パートナーセールス・アライアンス営業"}
          text={
            "自社の営業リソースだけで売上を拡大するのではなく、代理店、販売パートナー、グループ会社などの外部・内部チャネルを活用することで、営業成果を拡大する手法です。パートナーの選定、販売スキームの設計、協業体制の構築まで伴走できる人材が求められます。"
          }
        />

        <SubCategorySlider
          title="パートナーセールス"
          moreLink={`/client/scout/customers?q=パートナーセールス&p=1`}
          customers={partnerSalesCustomers}
          {...common}
        />
        <SubCategorySlider
          title="アライアンス営業"
          moreLink={`/client/scout/customers?q=アライアンス営業&p=1`}
          customers={allianceCustomers}
          {...common}
        />

        <NumberSectionWrapper />

        <CampaignSSection
          index={"3"}
          title={"営業組織の効率化・仕組み化"}
          text={
            "営業成果を継続的に高めるには、個人の経験や勘に頼るのではなく、営業プロセスを可視化し、再現性のある仕組みへと落とし込むことが必要です。SFA・CRMの活用、商談管理、ナーチャリングリストの整備、セールスイネーブルメントによる営業人材の育成・ナレッジ共有、AIやデジタルツールを活用した営業DXを通じて、組織全体の生産性向上と営業成果の最大化を実現します。"
          }
        />

        <SubCategorySlider
          title="営業組織開発"
          moreLink={`/client/scout/customers?q=営業組織開発&p=1`}
          customers={salesOrgDevCustomers}
          {...common}
        />
        <SubCategorySlider
          title="SFA、CRM活用"
          moreLink={`/client/scout/customers?q=SFA CRM&p=1`}
          customers={sfaCrmCustomers}
          {...common}
        />
        <SubCategorySlider
          title="セールスイネーブルメント（営業教育・仕組み化）"
          moreLink={`/client/scout/customers?q=セールスイネーブルメント&p=1`}
          customers={salesEnablementCustomers}
          {...common}
        />
        <SubCategorySlider
          title="営業DX"
          moreLink={`/client/scout/customers?q=営業DX&p=1`}
          customers={salesDxCustomers}
          {...common}
        />

        <NumberSectionWrapper />

        <CampaignSSection
          index={"4"}
          title={"インサイドセールス・カスタマーサクセス"}
          text={
            "顧客との継続的な接点を通じて、商談創出から既存顧客の活用促進までを担う領域です。インサイドセールス立ち上げや運用設計、顧客育成、カスタマーサクセス構築を通じて、LTV最大化と売上成長を実現します。"
          }
        />

        <SubCategorySlider
          title="インサイドセールス"
          moreLink={`/client/scout/customers?q=インサイドセールス&p=1`}
          customers={insideSalesCustomers}
          {...common}
        />
        <SubCategorySlider
          title="カスタマーサクセス"
          moreLink={`/client/scout/customers?q=カスタマーサクセス&p=1`}
          customers={customerSuccessCustomers}
          {...common}
        />
        <SubCategorySlider
          title="LTV最大化"
          moreLink={`/client/scout/customers?q=LTV最大化&p=1`}
          customers={ltvCustomers}
          {...common}
        />

        <NumberSectionWrapper />

        <CampaignSSection
          index={"5"}
          title={"グループ間・部門間の営業連携"}
          text={
            "複数の事業部やグループ会社を持つ企業では、各組織が個別に営業活動を行うことで機会損失が発生する場合があります。顧客情報の共有、クロスセル設計、共同提案体制の構築などを通じて、グループ全体の営業効率と成果を最大化できる人材が求められます。"
          }
        />

        <SubCategorySlider
          title="グループ間営業連携"
          moreLink={`/client/scout/customers?q=グループ間営業連携&p=1`}
          customers={groupSalesCustomers}
          {...common}
        />
        <SubCategorySlider
          title="部門間連携"
          moreLink={`/client/scout/customers?q=部門間連携&p=1`}
          customers={interDeptCustomers}
          {...common}
        />
        <SubCategorySlider
          title="クロスセル設計"
          moreLink={`/client/scout/customers?q=クロスセル設計&p=1`}
          customers={crossSellCustomers}
          {...common}
        />
      </PageWrapper>
    </React.Fragment>
  );

  return res;
};

export default ClientSalesCampaignComponent;

// 各サブカテゴリの上部は30px空ける
const SubCategoryWrapper = styled.div`
  width: 100%;
  margin-top: 30px;
`;

// 番号セクション（②〜⑤）の上部は90px空ける
const NumberSectionWrapper = styled.div`
  width: 100%;
  margin-top: 90px;
`;

const SubTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  margin-bottom: 15px;
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    margin-bottom: 30px;
  }
`;

const SliderWrapper = styled.div`
  // react-slickの横幅の計算がうまく働いていないのでここで決めている感じ
  max-width: ${ConstCalc.CustomerSlideMaxWidthSp};
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    max-width: ${ConstCalc.CustomerSlideMaxWidthTablet};
  }
  @media screen and (min-width: ${ConstBreakPoint.PcDeskTop}) {
    max-width: ${ConstCalc.CustomerSlideMaxWidthPc};
  }

  // 全幅スライダーを縦に連続させるため、カードがはみ出さないよう
  // 固定heightではなくmin-heightにして下方向へ自然に伸ばす（縦の最小値は担保）
  & .slick-slider {
    min-height: 334px;
  }
`;

const SlideCustomerWrapper = styled.div`
  padding: 5px 15px;
`;



// WEBPACK FOOTER //
// ./src/presentation/component/client/campaign/client_sales_campaign.tsx